import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const EDGE_URL = `${SUPABASE_URL}/functions/v1/comment-actions`;

interface CommentItem {
  id: string;
  name: string;
  email?: string;
  message: string;
  likes: number;
  is_best: boolean;
  created_at: string;
  updated_at?: string;
  password_hash?: string;
}

const ITEMS_PER_PAGE = 10;

export default function CommentsSection() {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sortOrder, setSortOrder] = useState<'popular' | 'recent'>('recent');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Password verification modal
  const [passwordModal, setPasswordModal] = useState<{
    open: boolean;
    commentId: string;
    mode: 'edit' | 'delete';
  }>({ open: false, commentId: '', mode: 'edit' });
  const [modalPassword, setModalPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalShake, setModalShake] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Edit state (after password verified)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [likingIds, setLikingIds] = useState<Set<string>>(new Set());

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const callEdge = async (body: Record<string, unknown>) => {
    const res = await fetch(EDGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || '서버 오류');
    return data;
  };

  const fetchComments = useCallback(async () => {
    try {
      let query = supabase
        .from('comments')
        .select('id, name, message, likes, is_best, created_at, updated_at');
      if (sortOrder === 'popular') {
        query = query
          .order('is_best', { ascending: false })
          .order('likes', { ascending: false })
          .order('created_at', { ascending: false });
      } else {
        query = query
          .order('is_best', { ascending: false })
          .order('created_at', { ascending: false });
      }
      const { data, error } = await query;
      if (error) throw error;
      if (data) setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  }, [sortOrder]);

  const fetchLikedStatus = useCallback(async () => {
    try {
      const data = await callEdge({ action: 'get_liked_comments' });
      if (data.likedIds) setLikedCommentIds(new Set(data.likedIds));
    } catch (err) {
      console.error('Error fetching liked status:', err);
    }
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    fetchLikedStatus();
  }, [fetchLikedStatus]);

  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [sortOrder]);

  useEffect(() => {
    if (passwordModal.open) {
      setTimeout(() => passwordInputRef.current?.focus(), 100);
    }
  }, [passwordModal.open]);

  const openPasswordModal = (commentId: string, mode: 'edit' | 'delete') => {
    setPasswordModal({ open: true, commentId, mode });
    setModalPassword('');
    setModalError('');
    setModalLoading(false);
    setModalShake(false);
  };

  const closePasswordModal = () => {
    setPasswordModal({ open: false, commentId: '', mode: 'edit' });
    setModalPassword('');
    setModalError('');
    setModalLoading(false);
    setModalShake(false);
  };

  const handlePasswordVerify = async () => {
    if (!modalPassword.trim()) {
      setModalError('비밀번호를 입력해주세요.');
      setModalShake(true);
      setTimeout(() => setModalShake(false), 500);
      return;
    }

    setModalLoading(true);
    setModalError('');

    try {
      if (passwordModal.mode === 'edit') {
        await callEdge({
          action: 'verify_password',
          comment_id: passwordModal.commentId,
          password: modalPassword,
        });
        const target = comments.find((c) => c.id === passwordModal.commentId);
        setEditingId(passwordModal.commentId);
        setEditMessage(target?.message || '');
        setVerifiedPassword(modalPassword);
        closePasswordModal();
        showToast('success', '인증되었습니다. 메시지를 수정해주세요.');
      } else {
        await callEdge({
          action: 'delete_comment',
          comment_id: passwordModal.commentId,
          password: modalPassword,
        });
        closePasswordModal();
        await fetchComments();
        showToast('success', '메시지가 삭제되었습니다.');
      }
    } catch (error: any) {
      setModalError(error.message || '비밀번호가 일치하지 않습니다.');
      setModalShake(true);
      setTimeout(() => setModalShake(false), 500);
      setModalPassword('');
      setTimeout(() => passwordInputRef.current?.focus(), 100);
    } finally {
      setModalLoading(false);
    }
  };

  const handleLike = async (commentId: string) => {
    if (likingIds.has(commentId)) return;
    setLikingIds((prev) => new Set(prev).add(commentId));

    const wasLiked = likedCommentIds.has(commentId);

    setLikedCommentIds((prev) => {
      const n = new Set(prev);
      if (wasLiked) n.delete(commentId);
      else n.add(commentId);
      return n;
    });
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? { ...c, likes: c.likes + (wasLiked ? -1 : 1) }
          : c
      )
    );

    try {
      const data = await callEdge({ action: 'toggle_like', comment_id: commentId });
      if (data.liked) {
        setLikedCommentIds((prev) => new Set(prev).add(commentId));
      } else {
        setLikedCommentIds((prev) => {
          const n = new Set(prev);
          n.delete(commentId);
          return n;
        });
      }
      await fetchComments();
    } catch (error: any) {
      setLikedCommentIds((prev) => {
        const n = new Set(prev);
        if (wasLiked) n.add(commentId);
        else n.delete(commentId);
        return n;
      });
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, likes: c.likes + (wasLiked ? 1 : -1) }
            : c
        )
      );
      showToast('error', error.message || '좋아요 처리 중 오류가 발생했습니다.');
    } finally {
      setLikingIds((prev) => {
        const n = new Set(prev);
        n.delete(commentId);
        return n;
      });
    }
  };

  const handleEditSave = async () => {
    if (!editingId || !editMessage.trim()) return;
    setEditSaving(true);
    try {
      await callEdge({
        action: 'edit_comment',
        comment_id: editingId,
        password: verifiedPassword,
        message: editMessage,
      });
      setEditingId(null);
      setEditMessage('');
      setVerifiedPassword('');
      await fetchComments();
      showToast('success', '메시지가 수정되었습니다.');
    } catch (error: any) {
      showToast('error', error.message || '수정 중 오류가 발생했습니다.');
    } finally {
      setEditSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditMessage('');
    setVerifiedPassword('');
  };

  return (
    <section id="comments" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Nanum Myeongjo', serif" }}
          >
            응원 메시지
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-gray-600">
            사법정의 회복을 위한 여러분의 목소리
          </p>
        </div>

        {/* Sort & Count */}
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <p className="text-sm text-gray-500">
            총 <span className="font-bold text-gray-900">{comments.length}</span>개의 메시지
          </p>
          <div className="flex items-center bg-white rounded-full p-1 shadow-sm border border-gray-200">
            <button
              onClick={() => setSortOrder('popular')}
              className={`flex items-center px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                sortOrder === 'popular' ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-fire-line mr-1 w-4 h-4 flex items-center justify-center"></i>최신순
            </button>
            <button
              onClick={() => setSortOrder('recent')}
              className={`flex items-center px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                sortOrder === 'recent' ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-time-line mr-1 w-4 h-4 flex items-center justify-center"></i>최신순
            </button>
          </div>
        </div>

        {/* Comment List */}
        <div className="space-y-3 md:space-y-4">
          {comments.length === 0 ? (
            <div className="bg-white rounded-xl p-8 md:p-12 text-center shadow-lg">
              <i className="ri-chat-3-line text-5xl md:text-6xl text-gray-300 mb-4"></i>
              <p className="text-sm md:text-base text-gray-500">
                아직 등록된 응원 메시지가 없습니다.
              </p>
            </div>
          ) : (
            <>
              {comments.slice(0, visibleCount).map((comment) => {
                const isLiked = likedCommentIds.has(comment.id);
                const isLiking = likingIds.has(comment.id);
                const isEditing = editingId === comment.id;

                return (
                  <div
                    key={comment.id}
                    className={`bg-white rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow ${
                      comment.is_best ? 'border-2 border-red-600' : ''
                    }`}
                  >
                    {comment.is_best && (
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                          <i className="ri-star-fill mr-1"></i>베스트
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm md:text-base font-bold text-gray-900">{comment.name}</p>
                        <p className="text-[10px] md:text-xs text-gray-500">
                          {new Date(comment.created_at).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {comment.updated_at && comment.updated_at !== comment.created_at && (
                            <span className="ml-1 text-gray-400">(수정됨)</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1.5 md:space-x-2">
                        {!isEditing && (
                          <>
                            <button
                              onClick={() => openPasswordModal(comment.id, 'edit')}
                              className="flex items-center px-2 md:px-2.5 py-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                              title="수정"
                            >
                              <i className="ri-edit-line text-xs md:text-sm"></i>
                            </button>
                            <button
                              onClick={() => openPasswordModal(comment.id, 'delete')}
                              className="flex items-center px-2 md:px-2.5 py-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="삭제"
                            >
                              <i className="ri-delete-bin-line text-xs md:text-sm"></i>
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleLike(comment.id)}
                          disabled={isLiking}
                          className={`flex items-center space-x-1.5 md:space-x-2 px-2.5 md:px-3 py-1.5 rounded-lg transition-all cursor-pointer group ${
                            isLiked
                              ? 'bg-red-50 text-red-600 scale-105'
                              : 'bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600'
                          } ${isLiking ? 'opacity-60 pointer-events-none' : ''}`}
                        >
                          <i
                            className={`transition-transform ${
                              isLiked
                                ? 'ri-heart-fill text-red-600 text-xs md:text-sm animate-[heartPop_0.3s_ease-out]'
                                : 'ri-heart-line group-hover:text-red-600 text-xs md:text-sm'
                            }`}
                          ></i>
                          <span
                            className={`text-xs md:text-sm font-semibold ${
                              isLiked ? 'text-red-600' : 'group-hover:text-red-600'
                            }`}
                          >
                            {comment.likes}
                          </span>
                        </button>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            <i className="ri-shield-check-line mr-1"></i>본인 인증 완료
                          </span>
                        </div>
                        <textarea
                          value={editMessage}
                          onChange={(e) => {
                            if (e.target.value.length <= 500) setEditMessage(e.target.value);
                          }}
                          maxLength={500}
                          rows={3}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                        />
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">{editMessage.length}/500</p>
                          <div className="flex space-x-2">
                            <button
                              onClick={cancelEdit}
                              className="px-3 md:px-4 py-1.5 text-xs md:text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                            >
                              취소
                            </button>
                            <button
                              onClick={handleEditSave}
                              disabled={editSaving || !editMessage.trim()}
                              className="px-3 md:px-4 py-1.5 text-xs md:text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-400 cursor-pointer whitespace-nowrap"
                            >
                              {editSaving ? '저장 중...' : '저장'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {comment.message}
                      </p>
                    )}
                  </div>
                );
              })}

              {visibleCount < comments.length && (
                <div className="flex justify-center pt-4 md:pt-6">
                  <button
                    onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
                    className="flex items-center space-x-2 px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-red-300 hover:text-red-600 transition-all shadow-sm hover:shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-down-line"></i>
                    <span>더보기 (+{comments.length - visibleCount}개)</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Password Verification Modal */}
      {passwordModal.open && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePasswordModal();
          }}
        >
          <div
            className={`bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden transition-transform ${
              modalShake ? 'animate-[shake_0.5s_ease-in-out]' : ''
            }`}
            style={{
              animation: modalShake ? 'shake 0.4s ease-in-out' : undefined,
            }}
          >
            <div className="p-6">
              <div className="flex flex-col items-center text-center mb-5">
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-full mb-3 ${
                    passwordModal.mode === 'delete' ? 'bg-red-100' : 'bg-amber-100'
                  }`}
                >
                  <i
                    className={`text-2xl ${
                      passwordModal.mode === 'delete'
                        ? 'ri-delete-bin-line text-red-600'
                        : 'ri-lock-line text-amber-600'
                    }`}
                  ></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  {passwordModal.mode === 'delete' ? '메시지 삭제' : '본인 확인'}
                </h3>
                <p className="text-sm text-gray-500 mt-1.5">
                  {passwordModal.mode === 'delete'
                    ? '삭제하면 복구할 수 없습니다.'
                    : '수정하려면 작성 시 입력한 비밀번호를 확인해주세요.'}
                </p>
              </div>

              <div className="relative mb-3">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-password-line text-sm"></i>
                </div>
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={modalPassword}
                  onChange={(e) => {
                    setModalPassword(e.target.value);
                    if (modalError) setModalError('');
                  }}
                  placeholder="비밀번호 입력"
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent text-sm transition-colors ${
                    modalError
                      ? 'border-red-400 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-red-500'
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !modalLoading) handlePasswordVerify();
                  }}
                />
              </div>

              {modalError && (
                <div className="flex items-center gap-1.5 px-3 py-2 bg-red-50 rounded-lg mb-3">
                  <i className="ri-error-warning-fill text-red-500 text-sm"></i>
                  <p className="text-xs text-red-600 font-medium">{modalError}</p>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={closePasswordModal}
                  disabled={modalLoading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={handlePasswordVerify}
                  disabled={modalLoading || !modalPassword.trim()}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2 ${
                    passwordModal.mode === 'delete'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                >
                  {modalLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      <span>확인 중...</span>
                    </>
                  ) : passwordModal.mode === 'delete' ? (
                    '삭제'
                  ) : (
                    '확인'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg flex items-center space-x-2 text-sm font-medium transition-all ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}></i>
          <span>{toast.text}</span>
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
        @keyframes heartPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.4); }
          100% { transform: scale(1); }
        }
      `}</style>
    </section>
  );
}
